/**
 * UpcomingDeadlines - Sidebar Widget
 * Shows next 3-5 upcoming deadlines with urgency indicators
 */

import React, { useMemo } from 'react';
import { Calendar, AlertCircle, Clock, Bell } from 'lucide-react';
import { format, parseISO, differenceInDays, isAfter, isBefore } from 'date-fns';

const UpcomingDeadlines = ({
  colleges = [],
  timeline = [],
  maxDisplay = 5,
  onDeadlineClick,
  className = ''
}) => {
  // Collect all deadlines from colleges and timeline
  const allDeadlines = useMemo(() => {
    const deadlines = [];
    const today = new Date();

    // Add college application deadlines
    colleges.forEach(college => {
      const gc = college.global_colleges;
      if (!gc) return;

      // ED Deadline
      if (gc.ed_deadline) {
        deadlines.push({
          id: `${college.id}_ed`,
          type: 'application',
          title: `${college.name} - Early Decision`,
          date: parseISO(gc.ed_deadline),
          college_id: college.id,
          college_name: college.name,
          round: 'ED'
        });
      }

      // EA Deadline
      if (gc.ea_deadline) {
        deadlines.push({
          id: `${college.id}_ea`,
          type: 'application',
          title: `${college.name} - Early Action`,
          date: parseISO(gc.ea_deadline),
          college_id: college.id,
          college_name: college.name,
          round: 'EA'
        });
      }

      // RD Deadline
      if (gc.rd_deadline) {
        deadlines.push({
          id: `${college.id}_rd`,
          type: 'application',
          title: `${college.name} - Regular Decision`,
          date: parseISO(gc.rd_deadline),
          college_id: college.id,
          college_name: college.name,
          round: 'RD'
        });
      }
    });

    // Add deadline reminder tasks ONLY (not regular tasks)
    timeline.forEach(task => {
      if (!task.due_date) return;

      // ONLY include deadline reminder tasks (created by trigger system)
      const isDeadlineReminder = task.source_type === 'deadline_reminder' ||
                                task.tags?.includes('watch_deadline');

      if (!isDeadlineReminder) return;

      const dueDate = parseISO(task.due_date);
      const daysUntil = differenceInDays(dueDate, today);

      // Only include upcoming deadlines (not old completed ones)
      if (daysUntil < -7 || task.status === 'completed') return;

      deadlines.push({
        id: task.id,
        type: 'deadline_reminder',
        title: task.title,
        date: dueDate,
        college_id: task.college_id,
        college_name: task.metadata?.college_name || task.college_name,
        task
      });
    });

    // Add general deadline milestones
    const currentYear = today.getFullYear();
    const academicYear = today.getMonth() >= 7 ? currentYear : currentYear - 1; // Academic year starts in August

    const generalDeadlines = [
      {
        id: 'sat_ea_ed',
        type: 'testing',
        title: 'Latest SAT for EA/ED Submission',
        date: new Date(academicYear, 8, 15), // Sept 15
      },
      {
        id: 'fafsa_opens',
        type: 'financial',
        title: 'FAFSA Opens',
        date: new Date(academicYear, 9, 1), // Oct 1
      },
      {
        id: 'earliest_ea',
        type: 'application',
        title: 'Earliest EA Deadline (estimate)',
        date: new Date(academicYear, 9, 15), // Oct 15
      },
      {
        id: 'rec_deadline',
        type: 'recommendation',
        title: 'Latest Date to Request Recommendation',
        date: new Date(academicYear, 9, 15), // Oct 15
      },
      {
        id: 'general_ed_ea',
        type: 'application',
        title: 'General ED/EA Deadline (estimate)',
        date: new Date(academicYear, 10, 1), // Nov 1
      },
      {
        id: 'sat_rd',
        type: 'testing',
        title: 'Latest SAT for RD Submission',
        date: new Date(academicYear, 10, 15), // Nov 15
      },
      {
        id: 'general_rd',
        type: 'application',
        title: 'General RD Deadline (estimate)',
        date: new Date(academicYear + 1, 0, 1), // Jan 1 next year
      }
    ];

    // Only add general deadlines that are upcoming
    generalDeadlines.forEach(gd => {
      const daysUntil = differenceInDays(gd.date, today);
      if (daysUntil >= -7 && daysUntil <= 180) { // Show if within next 6 months
        deadlines.push(gd);
      }
    });

    // Sort by date (soonest first)
    deadlines.sort((a, b) => a.date - b.date);

    // Take only the first maxDisplay items
    return deadlines.slice(0, maxDisplay);
  }, [colleges, timeline, maxDisplay]);

  // Calculate days until deadline and urgency
  const getDeadlineInfo = (deadline) => {
    const today = new Date();
    const daysUntil = differenceInDays(deadline.date, today);

    let urgency = 'normal';
    let urgencyColor = 'text-gray-600';
    let bgColor = 'bg-gray-50';
    let borderColor = 'border-gray-200';

    if (daysUntil < 0) {
      urgency = 'overdue';
      urgencyColor = 'text-red-700';
      bgColor = 'bg-red-50';
      borderColor = 'border-red-300';
    } else if (daysUntil <= 3) {
      urgency = 'critical';
      urgencyColor = 'text-orange-700';
      bgColor = 'bg-orange-50';
      borderColor = 'border-orange-300';
    } else if (daysUntil <= 7) {
      urgency = 'high';
      urgencyColor = 'text-yellow-700';
      bgColor = 'bg-yellow-50';
      borderColor = 'border-yellow-300';
    } else if (daysUntil <= 14) {
      urgency = 'medium';
      urgencyColor = 'text-blue-700';
      bgColor = 'bg-blue-50';
      borderColor = 'border-blue-300';
    }

    return {
      daysUntil,
      urgency,
      urgencyColor,
      bgColor,
      borderColor,
      dateText: format(deadline.date, 'MMM d, yyyy')
    };
  };

  // Get icon for deadline type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'application':
        return <Calendar className="w-4 h-4" />;
      case 'financial':
        return <AlertCircle className="w-4 h-4" />;
      case 'testing':
        return <Clock className="w-4 h-4" />;
      case 'essay':
      case 'recommendation':
      case 'task':
      default:
        return <Bell className="w-4 h-4" />;
    }
  };

  // Handle click
  const handleDeadlineClick = (deadline) => {
    if (onDeadlineClick) {
      onDeadlineClick(deadline);
    }
  };

  if (allDeadlines.length === 0) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 shadow-sm p-4 ${className}`}>
        <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-indigo-600" />
          Upcoming Deadlines
        </h3>
        <p className="text-sm text-gray-500">
          No upcoming deadlines
        </p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-indigo-600" />
          Upcoming Deadlines
        </h3>
      </div>

      {/* Deadline List */}
      <div className="divide-y divide-gray-200">
        {allDeadlines.map(deadline => {
          const info = getDeadlineInfo(deadline);

          return (
            <div
              key={deadline.id}
              onClick={() => handleDeadlineClick(deadline)}
              className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${info.bgColor} border-l-4 ${info.borderColor}`}
            >
              {/* Type Icon and Title */}
              <div className="flex items-start gap-2 mb-1">
                <div className={`flex-shrink-0 mt-0.5 ${info.urgencyColor}`}>
                  {getTypeIcon(deadline.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 line-clamp-2">
                    {deadline.title}
                  </p>
                </div>
              </div>

              {/* Date and Days Until */}
              <div className="flex items-center justify-between text-xs ml-6">
                <span className="text-gray-600">
                  {info.dateText}
                </span>
                <span className={`font-medium ${info.urgencyColor}`}>
                  {info.daysUntil < 0
                    ? `${Math.abs(info.daysUntil)} day${Math.abs(info.daysUntil) !== 1 ? 's' : ''} overdue`
                    : info.daysUntil === 0
                    ? 'Today'
                    : info.daysUntil === 1
                    ? 'Tomorrow'
                    : `${info.daysUntil} days`
                  }
                </span>
              </div>

              {/* College name (if applicable) */}
              {deadline.college_name && (
                <p className="text-xs text-gray-500 ml-6 mt-1">
                  {deadline.college_name}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer - View All Link */}
      {allDeadlines.length >= maxDisplay && (
        <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
          <button
            onClick={() => onDeadlineClick && onDeadlineClick({ type: 'view_all' })}
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          >
            View all deadlines →
          </button>
        </div>
      )}
    </div>
  );
};

export default UpcomingDeadlines;
